import { openPdf } from '../../../../../../entities/procedures/pdf/shared';
import { format } from 'date-fns';
import { createPdf } from '../../../../../../../vendor/pdfMake';
import { Room } from '../../../../../../../types/Room';

// Function to format time with am/pm
const formatTimeWithAmPm = (timeStr?: string): string => {
  if (!timeStr) return '';

  // Parse time string like "07:15" or "7:15"
  const parts = timeStr.split(':');
  if (parts.length !== 2) return timeStr;

  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];

  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${hours}:${minutes}${ampm}`;
};

// Function to extract side from procedure type
const extractSideFromProcedureType = (procedureType?: string): string => {
  if (!procedureType) return '';

  const lowerType = procedureType.toLowerCase();

  // Check for patterns like "L Left" or " L " or ending with "L"
  if (lowerType.includes(' l left') || lowerType.endsWith(' l') || lowerType.includes(' left')) {
    return 'L';
  }

  // Check for patterns like "R Right" or " R " or ending with "R"
  if (lowerType.includes(' r right') || lowerType.endsWith(' r') || lowerType.includes(' right')) {
    return 'R';
  }

  return '';
};

// Function to convert time string to minutes for proper sorting
const timeToMinutes = (timeStr?: string): number => {
  if (!timeStr) return 0;

  const parts = timeStr.split(':');
  if (parts.length !== 2) return 0;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Handle NaN cases
  if (isNaN(hours) || isNaN(minutes)) return 0;

  return hours * 60 + minutes;
};

const getContent = (hospitalName: string, date: Date, operationRooms: Room[]) => {
  // Collect all procedures from all rooms and sort by start time
  const allProcedures = operationRooms
    .flatMap(room => room.procedures.map(proc => ({ ...proc, roomName: room.name })))
    .sort((a, b) => {
      const timeMinutesA = timeToMinutes(a.startTimeText);
      const timeMinutesB = timeToMinutes(b.startTimeText);
      return timeMinutesA - timeMinutesB;
    });

  const tableBody = [
    // Header row
    [
      {
        text: 'Surgery Board  Front Desk View',
        colSpan: 5,
        alignment: 'left',
        bold: false,
        fontSize: 11,
        margin: [0, 4, 0, 4],
        border: [true, true, true, false],
      },
      '',
      '',
      '',
      '',
    ],
    [
      {
        text: format(date, 'ddd, MMM DD'),
        alignment: 'left',
        fontSize: 8,
        colSpan: 5,
        margin: [0, 2, 0, 4],
        border: [true, false, true, true],
      },
      '',
      '',
      '',
      '',
    ],
    // Column headers
    [
      { text: 'SOL START', style: 'tableHeader', fillColor: '#ffffff', bold: true },
      { text: 'PT NAME', style: 'tableHeader', fillColor: '#ffffff', bold: true },
      { text: 'SURGEON', style: 'tableHeader', fillColor: '#ffffff', bold: true },
      { text: 'SIDE', style: 'tableHeader', fillColor: '#ffffff', bold: true },
      { text: 'PROCEDURE', style: 'tableHeader', fillColor: '#ffffff', bold: true },
    ],
  ];

  // Add procedure rows
  allProcedures.forEach((procedure, index) => {
    const side = extractSideFromProcedureType(procedure.procedureType);
    const rowColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9';

    // Determine side color once
    let sideColor = 'black';
    if (side === 'R') sideColor = 'red';
    else if (side === 'L') sideColor = 'blue';

    tableBody.push([
      { text: formatTimeWithAmPm(procedure.startTimeText), style: 'tableCell', fillColor: rowColor },
      {
        text: procedure.patientNameUnformatted || procedure.patientName || '',
        style: 'tableCell',
        fillColor: rowColor,
      },
      { text: procedure.physician?.name || procedure.physicianName || '', style: 'tableCell', fillColor: rowColor },
      { text: side, style: 'tableCell', color: sideColor, bold: true, fillColor: rowColor },
      { text: procedure.procedureType || '', style: 'tableCell', fillColor: rowColor },
    ]);
  });

  return {
    table: {
      headerRows: 3,
      widths: ['10%', '18%', '18%', '8%', '*'],
      body: tableBody,
    },
    layout: {
      hLineWidth: function (i: number, node: any) {
        if (i === 0 || i === node.table.body.length) return 1;
        if (i === 3) return 1; // Line after headers
        return 0.5;
      },
      vLineWidth: function (i: number, node: any) {
        return i === 0 || i === node.table.widths.length ? 1 : 0.5;
      },
      hLineColor: function (i: number) {
        return i === 3 ? '#000000' : '#e0e0e0';
      },
      vLineColor: function () {
        return '#e0e0e0';
      },
      paddingLeft: function () {
        return 4;
      },
      paddingRight: function () {
        return 4;
      },
      paddingTop: function () {
        return 3;
      },
      paddingBottom: function () {
        return 3;
      },
    },
  };
};

export const BeaconSchedulePdfGenerator = (
  hospitalName: string,
  date: Date,
  _rooms: unknown, // Unused parameter kept for signature consistency with other generators
  operationRooms: Room[]
) => {
  const docDefinition = {
    pageSize: 'LETTER',
    pageOrientation: 'portrait' as const,
    pageMargins: [30, 30, 30, 30],
    defaultStyle: {
      fontSize: 9,
      lineHeight: 1.3,
      color: '#333333',
    },
    info: { title: `Surgery Board - Front Desk View - ${format(date, 'MM/DD/YYYY')}` },
    content: [getContent(hospitalName, date, operationRooms)],
    styles: {
      tableHeader: {
        bold: true,
        fontSize: 8,
        color: '#000000',
        alignment: 'left' as const,
        margin: [0, 2, 0, 2] as [number, number, number, number],
      },
      tableCell: {
        fontSize: 9,
        alignment: 'left' as const,
        margin: [0, 0, 0, 0] as [number, number, number, number],
      },
    },
  };

  openPdf(createPdf(docDefinition), `Surgery_Board_Front_Desk_${format(date, 'MM_DD_YYYY_HH_mm_ss')}`);
};
